import React, { useState, useEffect } from 'react'
import { restaurantService } from '../services/restaurantService'
import { vibeService, Vibe } from '../services/vibeService'
import { Restaurant } from '../lib/supabase'

const RestaurantManagement: React.FC = () => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [updating, setUpdating] = useState<string | null>(null)
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [vibes, setVibes] = useState<Vibe[]>([])
  const [selectedVibeIds, setSelectedVibeIds] = useState<string[]>([])
  const [updatingVibes, setUpdatingVibes] = useState(false)
  const [loadingVibes, setLoadingVibes] = useState(true)
  const [vibesError, setVibesError] = useState('')

  useEffect(() => {
    fetchRestaurants()
    fetchVibes()
  }, [])

  useEffect(() => {
    // Update selected vibe IDs when restaurant changes
    if (selectedRestaurant) {
      setSelectedVibeIds(selectedRestaurant.vibe_id || [])
    }
  }, [selectedRestaurant])

  useEffect(() => {
    // Refetch vibes when modal opens if not already loaded
    if (showModal && vibes.length === 0 && !loadingVibes && !vibesError) {
      fetchVibes()
    }
  }, [showModal])

  const fetchVibes = async () => {
    try {
      setLoadingVibes(true)
      setVibesError('')
      const data = await vibeService.getAllVibes()
      setVibes(data)
      if (data.length === 0) {
        setVibesError('No vibes found in database')
      }
    } catch (err) {
      console.error('Failed to fetch vibes:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch vibes'
      setVibesError(`Error loading vibes: ${errorMessage}`)
      setError(`Failed to fetch vibes: ${errorMessage}`)
    } finally {
      setLoadingVibes(false)
    }
  }

  const fetchRestaurants = async () => {
    try {
      setLoading(true)
      const data = await restaurantService.getAllRestaurants()
      setRestaurants(data)
    } catch (err) {
      setError('Failed to fetch restaurants')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (restaurant: Restaurant, status: Restaurant['status']) => {
    // If approving, check if vibes are selected
    if (status === 'approved') {
      const currentVibeIds = restaurant.vibe_id || []
      if (currentVibeIds.length === 0) {
        setError('Please select at least one vibe before approving the restaurant')
        return
      }
    }
    
    const id = restaurant.restaurant_id
    try {
      setUpdating(id)
      await restaurantService.updateRestaurantStatus(id, status)
      await fetchRestaurants() // Refresh the list
    } catch (err) {
      setError('Failed to update restaurant status')
    } finally {
      setUpdating(null)
    }
  }

  const handleVibeToggle = (vibeId: string) => {
    setSelectedVibeIds(prev => {
      if (prev.includes(vibeId)) {
        return prev.filter(id => id !== vibeId)
      } else {
        return [...prev, vibeId]
      }
    })
  }

  const handleSaveVibes = async () => {
    if (!selectedRestaurant) return
    
    if (selectedVibeIds.length === 0) {
      setError('Please select at least one vibe')
      return
    }
    
    try {
      setUpdatingVibes(true)
      setError('')
      const updatedRestaurant = await restaurantService.updateRestaurantVibes(selectedRestaurant.restaurant_id, selectedVibeIds)
      // Update the selected restaurant with new vibe data
      setSelectedRestaurant(updatedRestaurant)
      await fetchRestaurants() // Refresh the list
    } catch (err) {
      setError('Failed to update restaurant vibes')
    } finally {
      setUpdatingVibes(false)
    }
  }

  // Check if vibes have been saved (exist in restaurant)
  const hasSavedVibes = selectedRestaurant?.vibe_id && selectedRestaurant.vibe_id.length > 0
  
  // Check if current selection differs from saved vibes
  const vibesChanged = () => {
    if (!selectedRestaurant?.vibe_id) return selectedVibeIds.length > 0
    const savedIds = selectedRestaurant.vibe_id.sort()
    const currentIds = [...selectedVibeIds].sort()
    return JSON.stringify(savedIds) !== JSON.stringify(currentIds)
  }

  const getStatusColor = (status: Restaurant['status']) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'paused':
        return 'bg-yellow-100 text-yellow-800'
      case 'pending':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatTimeTo12Hour = (timeString: string) => {
    if (!timeString) return timeString
    
    // Handle time format like "HH:MM:SS" or "HH:MM"
    const timeParts = timeString.split(':')
    if (timeParts.length < 2) return timeString
    
    const hours = parseInt(timeParts[0], 10)
    const minutes = timeParts[1]
    
    if (isNaN(hours)) return timeString
    
    const period = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
    
    return `${displayHours}:${minutes} ${period}`
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Restaurant Management</h1>
        <p className="text-sm sm:text-base text-gray-600">Manage restaurant applications and their status</p>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              All Restaurants ({restaurants.length})
            </h3>
            <button
              onClick={fetchRestaurants}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Refresh
            </button>
          </div>

          {restaurants.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No restaurants found.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Restaurant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {restaurants.map((restaurant) => (
                      <tr 
                        key={restaurant.restaurant_id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedRestaurant(restaurant)
                          setShowModal(true)
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {restaurant.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {restaurant.address_line1}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{restaurant.city}</div>
                          <div className="text-sm text-gray-500">{restaurant.state}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{restaurant.contact_email}</div>
                          <div className="text-sm text-gray-500">{restaurant.contact_phone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(restaurant.status)}`}>
                            {restaurant.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(restaurant.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {restaurants.map((restaurant) => (
                  <div
                    key={restaurant.restaurant_id}
                    className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      setSelectedRestaurant(restaurant)
                      setShowModal(true)
                    }}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 truncate">{restaurant.name}</h3>
                        <p className="text-sm text-gray-500 truncate">{restaurant.address_line1}</p>
                      </div>
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0 ${getStatusColor(restaurant.status)}`}>
                        {restaurant.status}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center text-gray-600">
                        <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="truncate">{restaurant.city}, {restaurant.state}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="truncate">{restaurant.contact_email}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span>{restaurant.contact_phone}</span>
                      </div>
                      <div className="flex items-center text-gray-500 text-xs pt-1">
                        <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{formatDate(restaurant.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Restaurant Details Modal */}
      {showModal && selectedRestaurant && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setShowModal(false)}>
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center z-10">
              <div className="flex-1 min-w-0 pr-2">
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{selectedRestaurant.name}</h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content */}
            <div className="px-4 sm:px-6 py-4">
              <div className="space-y-4">
                {/* Basic Information and Images Side by Side */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Basic Information */}
                  <div className="lg:col-span-2 bg-gray-50 rounded-lg p-3 sm:p-4">
                    <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">Basic Information</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Restaurant Name</label>
                      <p className="text-sm font-semibold text-gray-900">{selectedRestaurant.name}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedRestaurant.status)}`}>
                        {selectedRestaurant.status}
                      </span>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Contact Email</label>
                      <p className="text-sm text-gray-900">{selectedRestaurant.contact_email}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Contact Phone</label>
                      <p className="text-sm text-gray-900">{selectedRestaurant.contact_phone}</p>
                    </div>
                    {selectedRestaurant.owner_user_id && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Owner User ID</label>
                        <p className="text-sm text-gray-900 font-mono text-xs">{selectedRestaurant.owner_user_id}</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Price Range</label>
                      <p className="text-sm text-gray-900 capitalize">{selectedRestaurant.price_range}</p>
                    </div>
                    {selectedRestaurant.cost_for_two !== undefined && selectedRestaurant.cost_for_two !== null && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Cost for Two</label>
                        <p className="text-sm text-gray-900">₹{selectedRestaurant.cost_for_two.toFixed(2)}</p>
                      </div>
                    )}
                    {selectedRestaurant.rating !== undefined && selectedRestaurant.rating !== null && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Rating</label>
                        <p className="text-sm text-gray-900">{selectedRestaurant.rating.toFixed(2)} ⭐</p>
                      </div>
                    )}
                    {selectedRestaurant.auto_approve_reservation !== undefined && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Auto Approve Reservation</label>
                        <p className="text-sm text-gray-900">{selectedRestaurant.auto_approve_reservation ? 'Yes' : 'No'}</p>
                      </div>
                    )}
                    {selectedRestaurant.max_table_book !== undefined && selectedRestaurant.max_table_book !== null && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Max Table Book</label>
                        <p className="text-sm text-gray-900">{selectedRestaurant.max_table_book}</p>
                      </div>
                    )}
                    </div>
                  </div>

                  {/* Images Section */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-base font-semibold text-gray-900 mb-3">Images</h4>
                    <div className="space-y-3">
                      {selectedRestaurant.profile_image && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Profile Image</label>
                          <img src={selectedRestaurant.profile_image} alt={selectedRestaurant.name} className="h-24 w-full rounded-lg object-cover shadow-sm" />
                        </div>
                      )}
                      {selectedRestaurant.images && selectedRestaurant.images.length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Restaurant Images ({selectedRestaurant.images.length})</label>
                          <div className="grid grid-cols-2 gap-2">
                            {selectedRestaurant.images.slice(0, 4).map((image, index) => (
                              <img key={index} src={image} alt={`${selectedRestaurant.name} ${index + 1}`} className="h-20 w-full rounded-lg object-cover shadow-sm" />
                            ))}
                          </div>
                          {selectedRestaurant.images.length > 4 && (
                            <p className="text-xs text-gray-500 mt-1">+{selectedRestaurant.images.length - 4} more</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-base font-semibold text-gray-900 mb-3">Address Information</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div >
                      <label className="block text-xs font-medium text-gray-500 mb-1">Address: </label>
                      <p className="text-sm text-gray-900">{selectedRestaurant.address_line1}, {selectedRestaurant.city},{selectedRestaurant.state}, {selectedRestaurant.pin_code} </p>
                    </div>
                    <div >
                    {selectedRestaurant.latitude !== undefined && selectedRestaurant.latitude !== null && selectedRestaurant.longitude !== undefined && selectedRestaurant.longitude !== null&& (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Latitude, Longitude</label>
                        <p className="text-sm text-gray-900 font-mono text-xs">({selectedRestaurant.latitude}, {selectedRestaurant.longitude})</p>
                      </div>
                    )}
                    </div>
                  </div>
                </div>

                {/* Operating Hours */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-base font-semibold text-gray-900 mb-3">Operating Hours</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Opening Hours</label>
                      <p className="text-sm text-gray-900">{formatTimeTo12Hour(selectedRestaurant.opening_hours)} - {formatTimeTo12Hour(selectedRestaurant.closing_hours)}</p>
                    </div>
                    <div>
                    {selectedRestaurant.days_open && selectedRestaurant.days_open.length > 0 && (
                      <div className="col-span-3">
                        <label className="block text-xs font-medium text-gray-500 mb-2">Days Open</label>
                        <div className="flex flex-wrap gap-2">
                          {selectedRestaurant.days_open.map((day, index) => (
                            <span key={index} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              {day}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}</div>
                   
                  </div>
                </div>


                <div className="grid grid-cols-3 gap-3">
                {/* Restaurant Types */}
                {selectedRestaurant.restaurant_types && selectedRestaurant.restaurant_types.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-base font-semibold text-gray-900 mb-3">Restaurant Types</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedRestaurant.restaurant_types.map((type, index) => (
                        <span key={index} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Cuisines */}
                {selectedRestaurant.cuisines && selectedRestaurant.cuisines.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-base font-semibold text-gray-900 mb-3">Cuisines</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedRestaurant.cuisines.map((cuisine, index) => (
                        <span key={index} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {cuisine}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Amenities */}
                {selectedRestaurant.amenities && selectedRestaurant.amenities.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-base font-semibold text-gray-900 mb-3">Amenities</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedRestaurant.amenities.map((amenity, index) => (
                        <span key={index} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                </div>

                {/* Vibes Section */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                      Vibes <span className="text-red-500">*</span>
                    </h4>
                    {selectedVibeIds.length === 0 && (
                      <span className="text-xs text-red-600 font-medium">Required for approval</span>
                    )}
                  </div>
                  <div className="space-y-3">
                    {loadingVibes ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                        <p className="text-sm text-gray-500">Loading vibes...</p>
                      </div>
                    ) : vibesError ? (
                      <div className="rounded-md bg-red-50 p-3">
                        <p className="text-sm text-red-700">{vibesError}</p>
                        <button
                          onClick={fetchVibes}
                          className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                        >
                          Retry
                        </button>
                      </div>
                    ) : vibes.length === 0 ? (
                      <div className="rounded-md bg-yellow-50 p-3">
                        <p className="text-sm text-yellow-700">No vibes available. Please add vibes to the database.</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {vibes.map((vibe) => (
                            <label
                              key={vibe.id}
                              className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                                selectedVibeIds.includes(vibe.id)
                                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedVibeIds.includes(vibe.id)}
                                onChange={() => handleVibeToggle(vibe.id)}
                                className="sr-only"
                              />
                              <span className="flex items-center gap-2">
                                {vibe.emoji && <span>{vibe.emoji}</span>}
                                <span>{vibe.name}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleSaveVibes}
                            disabled={updatingVibes || selectedVibeIds.length === 0 || !vibesChanged()}
                            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
                          >
                            {updatingVibes 
                              ? (hasSavedVibes ? 'Updating...' : 'Saving...') 
                              : (hasSavedVibes ? 'Update Vibes' : 'Save Vibes')
                            }
                          </button>
                          {selectedVibeIds.length > 0 && (
                            <span className="text-xs text-gray-600">
                              {selectedVibeIds.length} vibe{selectedVibeIds.length !== 1 ? 's' : ''} selected
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer with Action Buttons */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0">
              <button
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Close
              </button>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <button
                  onClick={async () => {
                    await handleStatusUpdate(selectedRestaurant, 'approved')
                    if (updating !== selectedRestaurant.restaurant_id) {
                      setShowModal(false)
                    }
                  }}
                  disabled={
                    updating === selectedRestaurant.restaurant_id || 
                    selectedRestaurant.status === 'approved' ||
                    !(selectedRestaurant.vibe_id && selectedRestaurant.vibe_id.length > 0)
                  }
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
                  title={
                    !(selectedRestaurant.vibe_id && selectedRestaurant.vibe_id.length > 0)
                      ? 'Please select and save at least one vibe before approving'
                      : ''
                  }
                >
                  {updating === selectedRestaurant.restaurant_id ? 'Updating...' : 'Approve'}
                </button>
                <button
                  onClick={async () => {
                    await handleStatusUpdate(selectedRestaurant, 'rejected')
                    if (updating !== selectedRestaurant.restaurant_id) {
                      setShowModal(false)
                    }
                  }}
                  disabled={updating === selectedRestaurant.restaurant_id || selectedRestaurant.status === 'rejected'}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600"
                >
                  {updating === selectedRestaurant.restaurant_id ? 'Updating...' : 'Reject'}
                </button>
                <button
                  onClick={async () => {
                    await handleStatusUpdate(selectedRestaurant, 'paused')
                    if (updating !== selectedRestaurant.restaurant_id) {
                      setShowModal(false)
                    }
                  }}
                  disabled={updating === selectedRestaurant.restaurant_id || selectedRestaurant.status === 'paused'}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-yellow-600"
                >
                  {updating === selectedRestaurant.restaurant_id ? 'Updating...' : 'Pause'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RestaurantManagement
